import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@material-ui/core/styles';

import { dayColor, getHeadObj, makeRowColumn } from 'lib/commonLib';
import {
  conversionComparDaily,
  conversionComparDailyGraph,
} from 'lib/api/conversion';

import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import SortLabelWrap from 'components/molecules/MbTable/SortLabelWrap';
import SpanningTableContents from 'components/molecules/MbTable/SpanningTableContents';
import BarGraph from 'components/organisms/Chart/BarGraph';
import NewCommonFilter from 'components/organisms/NewCommonFilter';

const DayStatistics = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [initialization, setInitialization] = useState(false);
  const initParams = {
    order: 'STATS_DTTM',
    sort: 'desc',
    sDate: moment().add(-8, 'day').format('YYYYMMDD'),
    eDate: moment().add(-1, 'day').format('YYYYMMDD'),
  };
  const [params, setParams] = useState(initParams);

  const graphList = [
    {
      title: t('세션전환수'),
      data: { bar: ['SESSION_CONV_CNT'], line: ['ADVER_ORDER_CNT'] },
      yAxisData: {
        left: { ADVER_ORDER_CNT: true },
        right: { SESSION_CONV_CNT: true },
      },
      customizeData: {
        SESSION_CONV_CNT: {
          keyLabel: t('모비온 세션전환수'),
          color: theme.palette.common.green,
        },
        ADVER_ORDER_CNT: { color: theme.palette.common.black },
      },
    },
    {
      title: t('세션전환율'),
      data: { bar: ['SESSION_CONV_RATE'], line: ['ADVER_CONV_RATE'] },
      yAxisData: {
        left: { ADVER_CONV_RATE: true },
        right: { SESSION_CONV_RATE: true },
      },
      customizeData: {
          SESSION_CONV_RATE: {
          isPercent: true,
          tooltipFormat: '0.00',
          keyLabel: t('모비온 세션전환율'),
          color: theme.palette.common.green,
        },
        ADVER_CONV_RATE: {
          isPercent: true,
          tooltipFormat: '0.00',
          color: theme.palette.common.black,
        },
      },
    },
    {
      title: t('세션전환매출'),
      data: {
        bar: [
          'BROWSER_SESSION_ORDER_AMT',
          'SESSION_ORDER_AMT',
          'DIRECT_ORDER_AMT',
        ],
        line: ['ADVER_ORDER_AMT'],
      },
      yAxisData: {
        left: { ADVER_ORDER_AMT: true },
        right: {
          BROWSER_SESSION_ORDER_AMT: true,
          SESSION_ORDER_AMT: true,
          DIRECT_ORDER_AMT: true,
        },
      },
      customizeData: {
        BROWSER_SESSION_ORDER_AMT: {
          color: theme.palette.common.green,
          stackId: 'BROWSER_SESSION_ORDER_AMT',
        },
        SESSION_ORDER_AMT: {
          color: theme.palette.common.blue,
          stackId: 'SESSION_ORDER_AMT',
        },
        DIRECT_ORDER_AMT: {
          stackId: 'DIRECT_ORDER_AMT',
        },
        ADVER_ORDER_AMT: { color: theme.palette.common.black },
      },
    },
  ];

  /**
   * 필터 초기화 후 값을 바꿈
   */
  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    } // eslint-disable-next-line
  }, [initialization]);

  /**
   * 페이지 파라미터를 관리하는 params을 수정하는 함수
   * @param newParams
   */
  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    if (JSON.stringify(params) !== JSON.stringify(initParams)) {
      setInitialization(true);
      setParams(initParams);
    }
  };

  const handleSortChange = (orderKey) => {
    const { order, sort } = params;
    const isDesc = order === orderKey && sort === 'desc';
    const converSort = isDesc ? 'asc' : 'desc';
    setParams({
      ...params,
      order: orderKey,
      sort: converSort,
    });
  };

  const customized = {
    STATS_DTTM: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'STATS_DTTM');
        return dayColor(rowObj['STATS_DTTM'], bodyItem1.type, bodyItem1.format);
      },
    },
    INF_CNT: {
      makeHead: (headsMap) => {
        return (
          <SpanningTableContents topTitle={t('광고주')} type={'head'}>
            <div style={{ width: 80 }}>
              <SortLabelWrap
                params={params}
                label={getHeadObj(headsMap, 'INF_CNT').label}
                orderKey={'INF_CNT'}
                onClick={(e) => handleSortChange('INF_CNT')}
              />
            </div>
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('전환수')}
                orderKey="ADVER_ORDER_CNT"
                onClick={(e) => handleSortChange('ADVER_ORDER_CNT')}
              />
            </div>
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('전환율')}
                orderKey="ADVER_CONV_RATE"
                onClick={(e) => handleSortChange('ADVER_CONV_RATE')}
              />
            </div>
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('전환매출')}
                orderKey="ADVER_ORDER_AMT"
                onClick={(e) => handleSortChange('ADVER_ORDER_AMT')}
              />
            </div>
          </SpanningTableContents>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'INF_CNT');
        const bodyItem2 = getHeadObj(headsMap, 'ADVER_ORDER_CNT');
        const bodyItem3 = getHeadObj(headsMap, 'ADVER_CONV_RATE');
        const bodyItem4 = getHeadObj(headsMap, 'ADVER_ORDER_AMT');
        return (
          <SpanningTableContents>
            <div style={{ width: 80 }}>
              {makeRowColumn(
                rowObj['INF_CNT'],
                bodyItem1.type,
                bodyItem1.format
              )}
            </div>
            <div style={{ width: 100 }}>
              {makeRowColumn(
                rowObj['ADVER_ORDER_CNT'],
                bodyItem2.type,
                bodyItem2.format
              )}
            </div>
            <div style={{ width: 100 }}>
              {makeRowColumn(
                rowObj['ADVER_CONV_RATE'],
                bodyItem3.type,
                '0.000%'
              )}
            </div>
            <div style={{ width: 100 }}>
              {makeRowColumn(
                rowObj['ADVER_ORDER_AMT'],
                bodyItem4.type,
                bodyItem4.format
              )}
            </div>
          </SpanningTableContents>
        );
      },
      orderFlag: false,
      headStyle: { borderLeft: '1px solid #e5e8eb' },
      bodyStyle: { borderLeft: '1px solid #e5e8eb' },
    },
    CLICK_CNT: {
      makeHead: (headsMap) => {
        return (
          <SpanningTableContents topTitle={t('모비온')} type={'head'}>
            <div style={{ width: 80 }}>
              <SortLabelWrap
                params={params}
                label={getHeadObj(headsMap, 'CLICK_CNT').label}
                orderKey="CLICK_CNT"
                onClick={(e) => handleSortChange('CLICK_CNT')}
              />
            </div>
            <div style={{ width: 80 }}>
              <SortLabelWrap
                params={params}
                label={getHeadObj(headsMap, 'SESSION_CONV_CNT').label}
                orderKey="SESSION_CONV_CNT"
                onClick={(e) => handleSortChange('SESSION_CONV_CNT')}
              />
            </div>
            <div style={{ width: 80 }}>
              <SortLabelWrap
                params={params}
                label={getHeadObj(headsMap, 'SESSION_CONV_RATE').label}
                orderKey="SESSION_CONV_RATE"
                onClick={(e) => handleSortChange('SESSION_CONV_RATE')}
              />
            </div>
            <div style={{ width: 80 }}>
              <SortLabelWrap
                  params={params}
                  label={getHeadObj(headsMap, 'SESSION_ORDER_AMT').label}
                  orderKey="SESSION_ORDER_AMT"
                  onClick={(e) => handleSortChange('SESSION_ORDER_AMT')}
              />
            </div>
            <div style={{ width: 120 }}>
              <SortLabelWrap
                params={params}
                label={getHeadObj(headsMap, 'BROWSER_SESSION_ORDER_AMT').label}
                orderKey="BROWSER_SESSION_ORDER_AMT"
                onClick={(e) => handleSortChange('BROWSER_SESSION_ORDER_AMT')}
              />
            </div>
            <div style={{ width: 80 }}>
              <SortLabelWrap
                params={params}
                label={getHeadObj(headsMap, 'DIRECT_ORDER_AMT').label}
                orderKey="DIRECT_ORDER_AMT"
                onClick={(e) => handleSortChange('DIRECT_ORDER_AMT')}
              />
            </div>
            <div style={{ width: 80 }}>
              <SortLabelWrap
                params={params}
                label={getHeadObj(headsMap, 'ORDER_AMT').label}
                orderKey="ORDER_AMT"
                onClick={(e) => handleSortChange('ORDER_AMT')}
              />
            </div>
          </SpanningTableContents>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'CLICK_CNT');
        const bodyItem2 = getHeadObj(headsMap, 'SESSION_CONV_CNT');
        const bodyItem3 = getHeadObj(headsMap, 'SESSION_CONV_RATE');
        const bodyItem4 = getHeadObj(headsMap, 'SESSION_ORDER_AMT');
        const bodyItem5 = getHeadObj(headsMap, 'DIRECT_ORDER_AMT');
        const bodyItem6 = getHeadObj(headsMap, 'ORDER_AMT');
        const bodyItem7 = getHeadObj(headsMap, 'BROWSER_SESSION_ORDER_AMT');
        return (
          <SpanningTableContents>
            <div style={{ width: 80 }}>
              {makeRowColumn(
                rowObj['CLICK_CNT'],
                bodyItem1.type,
                bodyItem1.format
              )}
            </div>
            <div style={{ width: 80 }}>
              {makeRowColumn(
                rowObj['SESSION_CONV_CNT'],
                bodyItem2.type,
                bodyItem2.format
              )}
            </div>
            <div style={{ width: 80 }}>
              {makeRowColumn(
                rowObj['SESSION_CONV_RATE'],
                bodyItem3.type,
                bodyItem3.format
              )}
            </div>
            <div style={{ width: 80 }}>
              {makeRowColumn(
                  rowObj['SESSION_ORDER_AMT'],
                  bodyItem4.type,
                  bodyItem4.format
              )}
            </div>
            <div style={{ width: 120 }}>
              {makeRowColumn(
                rowObj['BROWSER_SESSION_ORDER_AMT'],
                bodyItem7.type,
                bodyItem7.format
              )}
            </div>
            <div style={{ width: 80 }}>
              {makeRowColumn(
                rowObj['DIRECT_ORDER_AMT'],
                bodyItem5.type,
                bodyItem5.format
              )}
            </div>
            <div style={{ width: 80 }}>
              {makeRowColumn(
                rowObj['ORDER_AMT'],
                bodyItem6.type,
                bodyItem6.format
              )}
            </div>
          </SpanningTableContents>
        );
      },
      orderFlag: false,
      headStyle: { borderLeft: '1px solid #e5e8eb' },
      bodyStyle: { borderLeft: '1px solid #e5e8eb' },
    },
    BROWSER_SESSION_ORDER_AMT: { merged: true },
    ADVER_ORDER_AMT: { merged: true },
    ADVER_ORDER_CNT: { merged: true },
    ADVER_CONV_RATE: { merged: true },
    SESSION_CONV_CNT: { merged: true },
    SESSION_CONV_RATE: { merged: true },
    SESSION_ORDER_AMT: { merged: true },
    DIRECT_ORDER_AMT: { merged: true },
    ORDER_AMT: { merged: true },
  };

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          params={params}
          getParam={getParam}
          useExternalFilter={false}
          useDisplayFilter={false}
          usePltfomTpCodeFilter={false}
        />
      </NewFilterWrap>
      <BarGraph
        params={{ sDate: params.sDate, eDate: params.eDate }}
        dataReqPromise={conversionComparDailyGraph}
        graphList={graphList}
        title={t('전환비교[일자별] 그래프')}
        labelType="MMDD"
      />
      <NewTableContainer
        params={params}
        getParam={getParam}
        dataReqPromise={conversionComparDaily}
        customized={customized}
        useSearch={false}
      />
    </>
  );
};

export default DayStatistics;
